import { supabase, auth } from './supabase';

// 아이폰 홈 화면 앱 로그인 이어 주기 (대표 10/28 "가능하면 꼭 진행해") — Readme/PwaLoginHandoff.md
//
// 아이폰은 사파리 탭과 '홈 화면에 추가'한 앱의 저장소가 따로라 앱을 열면 다시 로그인해야 했다.
// '홈 화면에 추가' 순간 아이폰이 사파리의 쿠키만 앱에 복사하므로(시냅스 10/13 실기기·실손님 확인),
// 결과 잠금이 설치 안내를 띄울 때 15분 한 번용 코드를 쿠키에 넣고, 앱이 처음 열릴 때 그 코드로 로그인한다.
// 쿠키·주소에 로그인 정보(세션·토큰)는 절대 넣지 않는다 — 코드는 서버(pwa-login-handoff)에서만 쓸모가 있고 한 번 쓰면 끝.
// 10/7 대표 지시로 없앤 '앱을 열 때마다 최근 결과로 끌고 가는 장치'와 다르다: 설치 직후 첫 실행 한 번, 코드가 있을 때만.

const COOKIE_NAME = 'sdl_pwa_handoff';
const COOKIE_MAX_AGE_SECONDS = 15 * 60; // 서버 코드 수명과 같다
const REFRESH_AFTER_MS = 10 * 60 * 1000; // 설치 안내가 오래 떠 있으면 새 코드로 바꿔 둔다
const CALL_TIMEOUT_MS = 8000;
const CODE_PATTERN = /^[A-Za-z0-9_-]{32}$/;

/** 이어서 열 화면 — 같은 사이트 안 경로만(다른 사이트로 보내는 주소는 받지 않는다) */
export const isSafeResumePath = (path: unknown): path is string =>
    typeof path === 'string' && path.startsWith('/') && !path.startsWith('//') && !path.includes('\\') && path.length <= 300;

export interface PwaHandoffCookie {
    code: string;
    resumePath: string | null;
    createdAt: number;
}

export function readPwaHandoffCookie(cookieString: string = typeof document === 'undefined' ? '' : document.cookie): PwaHandoffCookie | null {
    const entry = cookieString.split(';').map((part) => part.trim()).find((part) => part.startsWith(`${COOKIE_NAME}=`));
    if (!entry) return null;
    try {
        const parsed = JSON.parse(decodeURIComponent(entry.slice(COOKIE_NAME.length + 1))) as { c?: unknown; r?: unknown; t?: unknown };
        if (typeof parsed.c !== 'string' || !CODE_PATTERN.test(parsed.c)) return null;
        return {
            code: parsed.c,
            resumePath: isSafeResumePath(parsed.r) ? parsed.r : null,
            createdAt: typeof parsed.t === 'number' ? parsed.t : 0,
        };
    } catch {
        return null;
    }
}

const writePwaHandoffCookie = (value: PwaHandoffCookie): void => {
    const payload = encodeURIComponent(JSON.stringify({ c: value.code, r: value.resumePath, t: value.createdAt }));
    document.cookie = `${COOKIE_NAME}=${payload}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Path=/; Secure; SameSite=Strict`;
};

export const clearPwaHandoffCookie = (): void => {
    if (typeof document === 'undefined') return;
    document.cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/; Secure; SameSite=Strict`;
};

/** 홈 화면에 추가한 앱으로 열렸는가(사파리 탭이 아님) */
export const isStandaloneDisplay = (): boolean => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(display-mode: standalone)').matches
        || (navigator as Navigator & { standalone?: boolean }).standalone === true;
};

const withTimeout = <T,>(promise: PromiseLike<T>, ms: number): Promise<T> => new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error('handoff timed out')), ms);
    Promise.resolve(promise).then(
        (value) => { window.clearTimeout(timer); resolve(value); },
        (error) => { window.clearTimeout(timer); reject(error); },
    );
});

const callHandoff = async (body: Record<string, unknown>): Promise<Record<string, unknown> | null> => {
    const { data, error } = await withTimeout(supabase.functions.invoke('pwa-login-handoff', { body }), CALL_TIMEOUT_MS);
    if (error) return null;
    return (data as Record<string, unknown> | null) ?? null;
};

/**
 * 사파리(로그인한 손님)에서 설치 안내를 띄울 때 — 코드를 쿠키에 넣어 둔다. 이미 넣은 코드가 10분 안이면 그대로 둔다.
 * 실패해도 아무것도 막지 않는다(앱에서 평소처럼 로그인하면 된다).
 */
export async function preparePwaLoginHandoff(resumePath: string | null): Promise<boolean> {
    if (typeof document === 'undefined' || isStandaloneDisplay()) return false;
    const current = readPwaHandoffCookie();
    if (current && Date.now() - current.createdAt < REFRESH_AFTER_MS && current.resumePath === (isSafeResumePath(resumePath) ? resumePath : null)) {
        return true;
    }
    try {
        const data = await callHandoff({ action: 'create' });
        const code = typeof data?.code === 'string' && CODE_PATTERN.test(data.code) ? data.code : null;
        if (!code) return false;
        writePwaHandoffCookie({ code, resumePath: isSafeResumePath(resumePath) ? resumePath : null, createdAt: Date.now() });
        return true;
    } catch (error) {
        console.warn('[pwaLoginHandoff] 코드 준비 실패:', error instanceof Error ? error.message : String(error));
        return false;
    }
}

const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

export type PwaHandoffResult = 'signed-in' | 'failed';

/**
 * 홈 화면 앱 첫 실행(로그인 전)에서 쿠키 코드로 로그인한다.
 * - 전화번호 회원: 평소처럼 인증번호를 요청 → 문자 훅이 문자 대신 번호를 잡아 둠 → 코드로 한 번 받아 → 평소 로그인과 같은 확인(verifyOtp)
 * - 이메일 회원: 서버가 준 한 번용 로그인 열쇠로 verifyOtp
 * verifyPhone은 화면의 로그인과 같은 함수(useAuthStore.verifyOtp — 차단 회원 확인 포함)를 받는다.
 */
export async function redeemPwaLoginHandoff(
    code: string,
    verifyPhone: (phone: string, otp: string) => Promise<unknown>,
): Promise<PwaHandoffResult> {
    const start = await callHandoff({ action: 'redeem', code });
    if (!start) return 'failed';

    if (start.method === 'magiclink' && typeof start.tokenHash === 'string' && start.tokenHash) {
        const { error } = await withTimeout(supabase.auth.verifyOtp({ type: 'magiclink', token_hash: start.tokenHash }), CALL_TIMEOUT_MS);
        return error ? 'failed' : 'signed-in';
    }

    if (start.method === 'sms' && typeof start.phone === 'string' && /^\+\d{8,15}$/.test(start.phone)) {
        const phone = start.phone;
        // 평소 로그인과 같은 인증번호 요청(손님 휴대폰 주소에서) — 이어 받기 중이라 문자는 가지 않는다
        await withTimeout(auth.signInWithPhone(phone), CALL_TIMEOUT_MS);
        for (let attempt = 0; attempt < 6; attempt++) {
            const claimed = await callHandoff({ action: 'claim', code });
            if (!claimed) return 'failed';
            if (typeof claimed.otp === 'string' && /^\d{4,10}$/.test(claimed.otp)) {
                await withTimeout(verifyPhone(phone, claimed.otp), CALL_TIMEOUT_MS);
                return 'signed-in';
            }
            await sleep(500);
        }
    }
    return 'failed';
}
